import { useGetDatasourcesQuery } from 'store/services/helios/dataSources';
import { useState, useCallback, useEffect, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetDatasourcesReq } from 'types';
import type { ModalsType } from 'types';
import { SearchField } from 'components/common/SearchField';
import { ActionModal } from 'components/mui/common/ActionModal';

import { useTableQueryControls } from 'utils/hooks';
import { transformDataByDataSchema } from 'utils/common';
import { Table } from 'components/common/Table/Table';
import { heliosDataSourcesModalDataScheme } from 'utils/table/admin/helios/dataSources';
import type { heliosDataSourcesDataScheme } from 'utils/table/admin/helios/dataSources';

interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

interface Filters {
  Name: string;
  WsUrlBasic: string;
  WsUrlDomain: string;
  WsUrlGoogle: string;
  WsProfile: string;
  TimeZoneId: string;
}

export interface SelectDatasourceModalProps extends ModalsType {
  title: string;
  defaultSelectedDatasource?: string;
  onConfirm: (selectedDatasource: string, selectedDatasourceName?: string) => void;
}

const INITIAL_FILTERS: Filters = {
  Name: '',
  WsUrlBasic: '',
  WsUrlDomain: '',
  WsUrlGoogle: '',
  WsProfile: '',
  TimeZoneId: '',
};

const INITIAL_SORT = {
  by: 'name' as const,
  order: 'asc' as const,
};

const INITIAL_PAGINATION = {
  page: 1,
  pageSize: 10,
};

const DATA_SOURCE_TABLE_HEIGHT = 'min(480px, 50vh)';
const DATA_SOURCE_MODAL_COLUMN_SIZE = 130;

const SelectDatasourceModalBase = (props: SelectDatasourceModalProps) => {
  const { open, title, defaultSelectedDatasource, onClose, onConfirm } = props;
  const { t } = useTranslation();
  const [selectedDatasource, setSelectedDatasource] = useState<string>(defaultSelectedDatasource || '');
  const [selectedDatasourceName, setSelectedDatasourceName] = useState<string | undefined>();
  const { state, setPagination, setSearch, setSort } = useTableQueryControls<typeof heliosDataSourcesModalDataScheme, Filters>(
    'heliosDataSourcesModal',
    {
      filters: INITIAL_FILTERS,
      sort: INITIAL_SORT,
      search: '',
      pagination: INITIAL_PAGINATION,
    }
  );

  const queryParams: GetDatasourcesReq<typeof heliosDataSourcesDataScheme> = {
    search: state?.search ?? '',
    page: state?.pagination?.page ?? 1,
    pageSize: state?.pagination?.pageSize ?? 10,
    orderBy: state?.sort?.by ?? 'name',
    orderDesc: state?.sort?.order === 'desc',
    ...INITIAL_FILTERS,
  };

  const { data: datasourcesData, isLoading: isDatasourcesLoading } = useGetDatasourcesQuery(queryParams, { skip: !open });
  useEffect(() => {
    const selectedDatasourceItem = datasourcesData?.items.find((item) => item.id === selectedDatasource);

    if (selectedDatasourceItem) setSelectedDatasourceName(selectedDatasourceItem.name);
  }, [datasourcesData?.items, selectedDatasource]);

  const transformedData = useMemo(
    () => transformDataByDataSchema(datasourcesData?.items ?? [], heliosDataSourcesModalDataScheme),
    [datasourcesData?.items]
  );

  const handleSort = useCallback(
    ({ column, direction }: SortConfig) =>
      setSort({
        by: column,
        order: direction,
      }),
    [setSort]
  );

  const handleConfirm = useCallback(() => {
    onConfirm(selectedDatasource, selectedDatasourceName);
  }, [onConfirm, selectedDatasource, selectedDatasourceName]);

  const handlePageSizeChange = (pageSize: number) => {
    setPagination({
      page: 1,
      pageSize,
    });
  };

  const handlePagination = (page: number) => {
    setPagination({
      page,
      pageSize: datasourcesData?.pageSize ?? INITIAL_PAGINATION.pageSize,
    });
  };

  const handleRowSelect = (selectedRow: string[]) => {
    const nextSelectedDatasource = selectedRow[0] ?? '';
    setSelectedDatasource(nextSelectedDatasource);
    setSelectedDatasourceName(datasourcesData?.items.find((item) => item.id === nextSelectedDatasource)?.name);
  };

  const renderControls = (
    <div className="w-full px-4 py-4">
      <SearchField
        placeholder={t('common.search.dataSources')}
        value={state?.search ?? ''}
        onChange={(event) => setSearch(event.target.value)}
        fullWidth
        dataTestId="admin-helios-data-source-search"
      />
    </div>
  );

  return (
    <ActionModal
      open={open}
      title={title}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleConfirm}
      isOnSubmitDisabled={!selectedDatasource}
      cancelText={t('common.cancel')}
      submitText={t('common.select')}
      maxWidth="xl"
      dataTestIds={{
        dialog: 'admin-helios-data-source-modal',
        cancelButton: 'admin-helios-data-source-modal-cancel-button',
        submitButton: 'admin-helios-data-source-modal-submit-button',
      }}>
      <div className="flex flex-col items-start justify-between w-full gap-4">
        <div className="w-full">
          <Table
            isLoading={isDatasourcesLoading}
            dataSchema={heliosDataSourcesModalDataScheme}
            data={transformedData}
            page={datasourcesData?.page}
            pageSize={datasourcesData?.pageSize}
            rows={datasourcesData?.totalCount ?? 0}
            sort={{
              column: state?.sort?.by ?? 'id',
              direction: state?.sort?.order ?? 'asc',
            }}
            isMultiSelect={false}
            controls={renderControls}
            defaultSelectedRows={selectedDatasource ? [selectedDatasource] : []}
            onSort={handleSort}
            onSelectRow={handleRowSelect}
            onPageSizeChange={handlePageSizeChange}
            onCurrentPageChange={handlePagination}
            fixedHeight={DATA_SOURCE_TABLE_HEIGHT}
            defaultColumnSize={DATA_SOURCE_MODAL_COLUMN_SIZE}
            isModal
            dataTestIdPrefix="admin-helios-data-source"
          />
        </div>
      </div>
    </ActionModal>
  );
};

export const SelectDatasourceModal = memo(SelectDatasourceModalBase);
